/**
 * Composed Mechanic Registry
 *
 * Map-based registry that maps config keys to composed mechanic JSON objects.
 * Mirrors campaignFramePatternRegistry.js pattern, but stores JSON configs
 * instead of React components.
 *
 * SRD composed mechanics are registered at app startup via registerSRDComposedMechanics().
 * Homebrew mechanics are registered dynamically after Firestore load.
 *
 * Key formats:
 * - Domain cards: kebab-case ('towering-stalk') or full ID ('core_domain_card_towering_stalk')
 * - Abilities: 'ability:{type}:{source}:{name}' (e.g., 'ability:class:Bard:Rally')
 *
 * @see composedMechanicConfig.js
 * @see AbilityRenderer.js (dual-path)
 * @see DomainCardRenderer.js (dual-path)
 */

const COMPOSED_REGISTRY = new Map();

/**
 * Normalize a domain card key to kebab-case for registry lookup.
 * Handles 'core_domain_card_towering_stalk' -> 'towering-stalk'
 * @param {string} key
 * @returns {string}
 */
const normalizeKey = (key) => {
  if (!key) return key;
  return key
    .replace('core_domain_card_', '')
    .replace(/_/g, '-');
};

/**
 * Register a single composed mechanic.
 * @param {string} configKey - The config key (kebab-case for domain cards, ability:... for abilities)
 * @param {object} mechanicJSON - The composed mechanic JSON object
 */
export const registerComposedMechanic = (configKey, mechanicJSON) => {
  COMPOSED_REGISTRY.set(configKey, mechanicJSON);
};

/**
 * Batch register multiple composed mechanics.
 * @param {Array<[string, object]>} entries - [[configKey, mechanicJSON], ...]
 */
export const registerComposedMechanics = (entries) => {
  entries.forEach(([key, json]) => registerComposedMechanic(key, json));
};

/**
 * Look up a composed mechanic by config key.
 * Tries exact match first, then normalized key (for domain card full IDs).
 * @param {string} configKey
 * @returns {object|null} Composed mechanic JSON or null
 */
export const getComposedMechanic = (configKey) => {
  // Exact match first
  const exact = COMPOSED_REGISTRY.get(configKey);
  if (exact) return exact;

  // Try normalized key (domain card full ID -> kebab-case)
  const normalized = normalizeKey(configKey);
  if (normalized !== configKey) {
    return COMPOSED_REGISTRY.get(normalized) || null;
  }

  return null;
};

/**
 * Check if a composed mechanic exists for a given key.
 * Used by shouldRenderWithDomainCardRenderer as a gatekeeper check.
 * @param {string} configKey
 * @returns {boolean}
 */
export const hasComposedMechanic = (configKey) => {
  return getComposedMechanic(configKey) !== null;
};

/**
 * List all registered config keys.
 * @returns {string[]}
 */
export const getRegisteredComposedKeys = () => [...COMPOSED_REGISTRY.keys()];

/**
 * Remove a single composed mechanic from the registry.
 */
export const removeComposedMechanic = (configKey) => {
  COMPOSED_REGISTRY.delete(configKey);
};

/**
 * Compute the registry key for a composed mechanic config.
 * Abilities: 'ability:homebrew:homebrew:{name}'
 * Domain cards: 'hb-{slugified-name}'
 * @param {object} config - Composed mechanic JSON
 * @returns {string|null}
 */
export const computeRegistryKey = (config) => {
  const name = config?.meta?.name;
  if (!name) return null;
  const context = config?.meta?.containerContext;
  if (context === 'domain-card') {
    return 'hb-' + name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
  }
  return `ability:homebrew:homebrew:${name}`;
};

/**
 * Clear all registered mechanics. For testing only.
 */
export const clearComposedMechanics = () => {
  COMPOSED_REGISTRY.clear();
};
